import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { apiClient } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { mapUserToInternalProficiency } from '../../utils/proficiencyMap';
import DiscoveryStep from './steps/DiscoveryStep';
import CapabilityStep from './steps/CapabilityStep';
import ExperienceStep from './steps/ExperienceStep';
import ReviewStep from './steps/ReviewStep';

interface TalentDiscoveryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type Step = 'discovery' | 'capability' | 'experience' | 'review';

const TalentDiscoveryModal: React.FC<TalentDiscoveryModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState<Step>('discovery');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Multi-step form data
    const [formData, setFormData] = useState({
        discovery: {
            enjoyDoing: [] as string[],
            haveDone: [] as string[],
            wantNow: [] as string[]
        },
        confirmation: {
            confirmedSkills: [] as Array<{
                name: string;
                category: string;
                proficiency: number;
            }>
        },
        experience: {
            workFrequency: undefined as 'daily' | 'weekly' | 'occasional' | 'first-time' | undefined,
            environment: undefined as 'indoor' | 'outdoor' | 'both' | undefined,
            nightWork: false,
            emergencyWork: false,
            shortNotice: false
        }
    });

    const steps: Step[] = ['discovery', 'capability', 'experience', 'review'];
    const currentStepIndex = steps.indexOf(currentStep);
    const progress = ((currentStepIndex + 1) / steps.length) * 100;

    const handleNext = () => {
        const nextIndex = currentStepIndex + 1;
        if (nextIndex < steps.length) {
            setCurrentStep(steps[nextIndex]);
        }
    };

    const handleBack = () => {
        const prevIndex = currentStepIndex - 1;
        if (prevIndex >= 0) {
            setCurrentStep(steps[prevIndex]);
        }
    };

    const handleSave = async () => {
        if (!user) return;

        setSaving(true);
        setError('');

        try {
            // Step 1: Ensure talent profile exists
            try {
                await apiClient.createTalentProfile(user.id, {
                    name: `${user.firstName} ${user.lastName}`,
                    headline: 'Talent Profile',
                    bio: '',
                    location: '',
                    status: 'active',
                    discoveryAnswers: formData.discovery,
                    availabilityTraits: {
                        nightWork: formData.experience.nightWork,
                        emergencyWork: formData.experience.emergencyWork,
                        shortNotice: formData.experience.shortNotice
                    },
                    skillCategories: [...new Set(formData.confirmation.confirmedSkills.map(s => s.category))] as any
                });
            } catch (profileErr: any) {
                // If conflict (already exists), that's fine
                if (profileErr.status !== 409 && !profileErr.message?.includes('exists')) {
                    throw profileErr;
                }
            }

            // Step 2: Add each confirmed skill
            const userId = user.id || (user as any)._id;
            if (!userId) {
                throw new Error('User identification failed. Please log in again.');
            }

            for (const skill of formData.confirmation.confirmedSkills) {
                try {
                    await apiClient.addUserSkill(userId, {
                        name: skill.name,
                        category: skill.category,
                        description: '',
                        proficiency: mapUserToInternalProficiency(skill.proficiency),
                        yearsOfExperience: formData.experience.workFrequency === 'daily' ? 2 :
                            formData.experience.workFrequency === 'weekly' ? 1 : 0.5,
                        lastUsed: new Date().toISOString()
                    });
                } catch (skillErr: any) {
                    // Log but don't stop the whole process
                    console.warn(`Failed to link skill ${skill.name}:`, skillErr.message);
                }
            }

            onSuccess();
            onClose();
            resetForm();
        } catch (err: any) {
            console.error('Failed to save talent profile:', err);
            setError(err.message || 'Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setCurrentStep('discovery');
        setFormData({
            discovery: {
                enjoyDoing: [],
                haveDone: [],
                wantNow: []
            },
            confirmation: {
                confirmedSkills: []
            },
            experience: {
                workFrequency: undefined,
                environment: undefined,
                nightWork: false,
                emergencyWork: false,
                shortNotice: false
            }
        });
        setError('');
    };

    const handleClose = () => {
        if (!saving) {
            onClose();
            // Don't reset form immediately - let user resume if they reopen
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleClose}
                        className="absolute inset-0 bg-black/85 backdrop-blur-md"
                    />

                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 30 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 30 }}
                        className="relative w-full max-w-4xl max-h-[92vh] mt-12 sm:mt-0 bg-[#1a1510] border border-premium-gold/30 rounded-2xl shadow-[0_30px_100px_rgba(0,0,0,0.8),0_0_50px_rgba(212,175,55,0.1)] overflow-hidden flex flex-col"
                    >
                        {/* Header - Fixed */}
                        <div className="flex-shrink-0 px-6 py-4 border-b border-premium-gold/20 bg-[#241e16]">
                            <div className="flex items-center justify-between mb-3">
                                <h2 className="font-serif text-2xl text-premium-gold">
                                    Discover Your Talents
                                </h2>
                                <button
                                    onClick={handleClose}
                                    disabled={saving}
                                    className="text-white/50 hover:text-white transition-colors disabled:opacity-50"
                                >
                                    <FiX size={24} />
                                </button>
                            </div>

                            {/* Progress Bar */}
                            <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-black/30 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 0.3 }}
                                        className="h-full bg-gradient-to-r from-premium-gold-dark to-premium-gold"
                                    />
                                </div>
                                <span className="text-xs text-white/60 font-mono">
                                    {currentStepIndex + 1}/{steps.length}
                                </span>
                            </div>

                            {/* Step Names */}
                            <div className="flex gap-2 mt-2">
                                {[
                                    { id: 'discovery', label: 'Discovery' },
                                    { id: 'capability', label: 'Confirm Skills' },
                                    { id: 'experience', label: 'Experience' },
                                    { id: 'review', label: 'Review' }
                                ].map((step, index) => (
                                    <div
                                        key={step.id}
                                        className={`flex-1 text-xs text-center py-1 rounded transition-all ${currentStepIndex >= index
                                            ? 'text-premium-gold bg-premium-gold/10'
                                            : 'text-white/30'
                                            }`}
                                    >
                                        {step.label}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Body - Scrollable */}
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                            {error && (
                                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-200 text-sm">
                                    {error}
                                </div>
                            )}

                            <AnimatePresence mode="wait">
                                {currentStep === 'discovery' && (
                                    <motion.div
                                        key="discovery"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                    >
                                        <DiscoveryStep
                                            data={formData.discovery}
                                            onUpdate={(data) => setFormData({ ...formData, discovery: data })}
                                            onNext={handleNext}
                                        />
                                    </motion.div>
                                )}

                                {currentStep === 'capability' && (
                                    <motion.div
                                        key="capability"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                    >
                                        <CapabilityStep
                                            discoveryData={formData.discovery}
                                            data={formData.confirmation}
                                            onUpdate={(data) => setFormData({ ...formData, confirmation: data })}
                                            onNext={handleNext}
                                            onBack={handleBack}
                                        />
                                    </motion.div>
                                )}

                                {currentStep === 'experience' && (
                                    <motion.div
                                        key="experience"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                    >
                                        <ExperienceStep
                                            data={formData.experience}
                                            onUpdate={(data) => setFormData({ ...formData, experience: data })}
                                            onNext={handleNext}
                                            onBack={handleBack}
                                        />
                                    </motion.div>
                                )}

                                {currentStep === 'review' && (
                                    <motion.div
                                        key="review"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                    >
                                        <ReviewStep
                                            allData={formData}
                                            onBack={handleBack}
                                            onSave={handleSave}
                                            saving={saving}
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default TalentDiscoveryModal;
